import type { ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

interface BlockerBlocked {
	state: 'blocked';
	reset(): void;
	proceed(): void;
}

interface BlockerUnblocked {
	state: 'unblocked';
	reset: undefined;
	proceed: undefined;
}

const IDLE_BLOCKER: BlockerUnblocked = {
	state: 'unblocked',
	reset: undefined,
	proceed: undefined,
};

interface BlockerProceeding {
	state: 'proceeding';
	reset(): void;
	proceed(): void;
}

type Blocker = BlockerUnblocked | BlockerBlocked | BlockerProceeding;

interface BlockerContext {
	blocker: Blocker;
	shouldBlock: boolean;
	setShouldBlock: ((shouldBlock: boolean) => void) | undefined;
	toggle: (() => void) | undefined;
}

const defaultBlockerContext: BlockerContext = {
	blocker: IDLE_BLOCKER,
	shouldBlock: false,
	setShouldBlock: undefined,
	toggle: undefined,
};

const ToggleRowBlockerContext = createContext<BlockerContext>(
	defaultBlockerContext
);

export function ToggleRowBlockerContextProvider({
	children,
	value,
}: {
	children: ReactNode;
	value: BlockerContext;
}) {
	return (
		<ToggleRowBlockerContext.Provider value={value}>
			{children}
		</ToggleRowBlockerContext.Provider>
	);
}

/**
 * In the RowRenderer, call `useToggleRowContext` with the function that toggles
 * the row, and wrap the children in the `ToggleRowBlockerContextProvider`. This
 * will allow the `useToggleRowBlocker` hook to control whether the user can
 * toggle the row.
 *
 * @example
 * const { toggleRowContext, tryToggleRow } = useToggleRowContext(toggleRowExpanded);
 *
 * return (
 *   <ToggleRowBlockerContextProvider value={toggleRowContext}>
 *     <div
 *       style={animatedRowStyle}
 *       onTransitionEnd={() => {
 *         toggleRowContext.blocker.reset?.();
 *       }}
 * 	   >
 * 		   {renderRow()}
 *     </div>
 *   </ToggleRowBlockerContextProvider>
 * );
 */
export function useToggleRowContext(toggle: () => void): {
	toggleRowContext: BlockerContext;
	tryToggleRow: () => boolean;
} {
	const [blocker, setBlocker] = useState<Blocker>(IDLE_BLOCKER);
	const [shouldBlock, setShouldBlock] = useState(false);

	const toggleRowContext = useMemo<BlockerContext>(
		() => ({
			blocker,
			shouldBlock,
			setShouldBlock,
			toggle,
		}),
		[blocker, toggle, shouldBlock]
	);

	return {
		toggleRowContext,
		tryToggleRow: useCallback(() => {
			if (blocker.state === 'unblocked') {
				if (shouldBlock) {
					const blockerBlocked: BlockerBlocked = {
						state: 'blocked',
						reset: () => {
							setBlocker(IDLE_BLOCKER);
						},
						proceed: () => {
							toggle();
							setBlocker(blockerProceeding);
						},
					};

					const blockerProceeding: BlockerProceeding = {
						...blockerBlocked,
						state: 'proceeding',
					};

					setBlocker(blockerBlocked);
					return false;
				} else {
					toggle();
					return true;
				}
			}
			return false;
		}, [blocker.state, toggle, shouldBlock]),
	};
}

/**
 * The `useToggleRowBlocker` hook allows you to prevent the user from
 * toggling the currently open row, and present them with a custom UI
 * to allow them to proceed.
 *
 * This is based on the `useBlocker` hook from React Router.
 * @see {@link https://reactrouter.com/en/main/hooks/use-blocker}
 */
export function useToggleRowBlocker(shouldBlock: boolean) {
	const { blocker, setShouldBlock } = useContext(ToggleRowBlockerContext);

	useEffect(() => {
		setShouldBlock?.(shouldBlock);
		return () => {
			setShouldBlock?.(false);
		};
	}, [setShouldBlock, shouldBlock]);

	useEffect(() => {
		return () => {
			blocker.reset?.();
		};
		// Reset blocker when unmounted
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return blocker;
}
